import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import FeaturedGames from './components/FeaturedGames';
import CategorySection from './components/CategorySection';
import ArticleSection from './components/ArticleSection';
import CTASection from './components/CTASection';

export default function Home() {
  useEffect(() => {
    document.title = '黄油游戏库 - 精选独立游戏一站式畅玩平台';
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <FeaturedGames />
        <CategorySection />
        <ArticleSection />

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                为什么选择黄油游戏库？
              </h2>
              <p className="text-sm text-foreground-600 mt-2 max-w-xl mx-auto">
                我们致力于为独立游戏爱好者提供最优质的体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '正版授权保障', desc: '所有游戏均经过正版授权，支持开发者，保障游戏生态健康发展' },
                { icon: 'ri-cloud-line', title: '云端存档同步', desc: '游戏进度云端自动保存，随时随地继续你的冒险旅程' },
                { icon: 'ri-team-line', title: '活跃玩家社区', desc: '50万+活跃玩家社区，交流攻略心得，分享游戏快乐' },
                { icon: 'ri-download-line', title: '一键极速下载', desc: '高速下载通道，智能节点选择，让等待成为过去' },
                { icon: 'ri-price-tag-3-line', title: '超值优惠活动', desc: '定期推出游戏折扣和免费领取活动，让好游戏触手可及' },
                { icon: 'ri-customer-service-2-line', title: '7x24小时客服', desc: '专业客服团队全天候待命，快速解决你的任何问题' },
              ].map((feature, i) => (
                <div key={i} className="bg-background-100 rounded-lg border border-background-200/70 p-6">
                  <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-lg text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">常见问题</h2>
              <p className="text-sm text-foreground-600 mt-2">关于黄油游戏库，你想知道的都在这里</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {[
                { q: '黄油游戏库是什么？', a: '黄油游戏库是一个专注于独立游戏的精选聚合平台。我们精心挑选高质量的独立游戏作品，为玩家提供游戏发现、下载和社区交流的一站式服务。' },
                { q: '黄油游戏库怎么用？', a: '使用黄油游戏库非常简单：浏览游戏分类或搜索感兴趣的游戏，点击游戏卡片查看详情，然后一键下载即可开始游玩。注册账号后还能使用云端存档和社区互动功能。' },
                { q: '黄油游戏库的游戏都是正版吗？', a: '是的，黄油游戏库上的所有游戏均经过正式授权，我们与众多独立游戏开发者保持紧密合作，确保每一款游戏都合法合规地提供给玩家。' },
                { q: '黄油游戏库支持哪些平台？', a: '目前黄油游戏库支持Windows、Mac和Linux三大桌面平台。部分游戏还支持Switch、PlayStation、Xbox等主机平台，以及iOS和Android移动平台。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 cursor-pointer">
                  <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 text-sm list-none">
                    {faq.q}
                    <div className="w-5 h-5 flex items-center justify-center group-open:rotate-180 transition-transform duration-200">
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '黄油游戏库 - 精选独立游戏一站式畅玩平台',
          description: '黄油游戏库是专为独立游戏爱好者打造的精选游戏聚合平台，汇聚海量高质量独立游戏资源。',
          url: 'https://lejiabaoli.cn',
          mainEntity: {
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '黄油游戏库是什么？', acceptedAnswer: { '@type': 'Answer', text: '黄油游戏库是一个专注于独立游戏的精选聚合平台。我们精心挑选高质量的独立游戏作品，为玩家提供游戏发现、下载和社区交流的一站式服务。' } },
              { '@type': 'Question', name: '黄油游戏库怎么用？', acceptedAnswer: { '@type': 'Answer', text: '使用黄油游戏库非常简单：浏览游戏分类或搜索感兴趣的游戏，点击游戏卡片查看详情，然后一键下载即可开始游玩。' } },
              { '@type': 'Question', name: '黄油游戏库的游戏都是正版吗？', acceptedAnswer: { '@type': 'Answer', text: '是的，黄油游戏库上的所有游戏均经过正式授权，每一款游戏都合法合规地提供给玩家。' } },
              { '@type': 'Question', name: '黄油游戏库支持哪些平台？', acceptedAnswer: { '@type': 'Answer', text: '目前黄油游戏库支持Windows、Mac和Linux三大桌面平台。部分游戏还支持Switch、PlayStation、Xbox等主机平台。' } },
            ],
          },
        })}
      </script>
    </>
  );
}